import React, { useEffect, useState } from "react";
import { Flame, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import StreakRewardModal from "./StreakRewardModal";

/**
 * Gentle daily-streak indicator for the Dashboard.
 *
 * • Pings `/progress/streak/ping` once on mount so today counts, then
 *   fetches `/progress/streak` for the UI-shaped week rail.
 * • Renders inline near the greeting: a flame chip with the current
 *   count + earned milestone badge pills + a 7-day dot rail.
 * • Whenever the fetched `milestones` list contains a threshold the
 *   browser hasn't already celebrated (tracked in localStorage), we
 *   surface the Warren shout-out reward modal — this is StrictMode-safe
 *   and also covers milestones unlocked on the server between visits.
 * • If the founder has no streak yet, shows a soft nudge instead of
 *   scolding them with a big red "0".
 */

const BADGES = {
  7: { label: "7-day", tone: "from-pink-300 to-pink-500", text: "text-white" },
  30: { label: "30-day", tone: "from-pink-300 to-pink-600", text: "text-white" },
};

const SEEN_KEY = "cpgStreakMilestonesSeen";

function readSeen() {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr.map(Number) : []);
  } catch {
    return new Set();
  }
}

function markSeen(list) {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(Array.from(list).map(Number)));
  } catch {
    /* private mode */
  }
}

export default function StreakChip() {
  const [state, setState] = useState(null);
  const [reward, setReward] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await api.post("/progress/streak/ping").catch(() => null);
        const { data } = await api.get("/progress/streak");
        if (cancelled) return;
        setState(data);

        const earned = (data?.milestones || []).filter((m) => BADGES[m]);
        const seen = readSeen();
        const unseen = earned.filter((m) => !seen.has(m));
        if (unseen.length > 0) {
          // Highest unseen milestone wins the on-screen shout-out.
          const highest = Math.max(...unseen);
          setReward(highest);
          // Persist immediately so a refresh or StrictMode remount doesn't
          // re-open the modal.
          earned.forEach((m) => seen.add(m));
          markSeen(seen);
        }
      } catch {
        /* silent — streak is a nice-to-have, don't block the dashboard */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!state) return null;

  const { current = 0, best = 0, week = [], milestones = [] } = state;
  const hasStreak = current > 0;
  const label = current === 1 ? "day" : "days";

  return (
    <>
      <div
        className="mt-5 flex flex-wrap items-center gap-3"
        data-testid="streak-chip"
      >
        {hasStreak ? (
          <div
            className="inline-flex items-center gap-2 rounded-full pl-2 pr-3.5 py-1.5 text-sm font-semibold text-pink-900 shadow-sm border border-pink-200"
            style={{
              background:
                "linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 55%, #FED7AA 100%)",
            }}
            data-testid="streak-current"
            title={best > current ? `Personal best: ${best} days` : "You're on your best streak — keep going!"}
          >
            <span
              className="inline-flex items-center justify-center w-6 h-6 rounded-full"
              style={{
                background: "linear-gradient(135deg, #FB923C 0%, #EA580C 100%)",
              }}
            >
              <Flame className="w-3.5 h-3.5 text-white" />
            </span>
            <span>
              {current} {label} streak
            </span>
          </div>
        ) : (
          <div
            className="inline-flex items-center gap-2 rounded-full pl-2 pr-3.5 py-1.5 text-sm font-medium text-slate-600 border border-slate-200 bg-white"
            data-testid="streak-nudge"
          >
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-100">
              <Sparkles className="w-3.5 h-3.5 text-slate-500" />
            </span>
            <span>Show up tomorrow to start your streak.</span>
          </div>
        )}

        {/* Earned milestone badges */}
        {milestones.length > 0 && (
          <div
            className="flex items-center gap-1.5"
            data-testid="streak-badges"
            aria-label="Earned streak badges"
          >
            {milestones.map((m) => {
              const b = BADGES[m];
              if (!b) return null;
              return (
                <span
                  key={m}
                  className={`inline-flex items-center gap-1 rounded-full bg-gradient-to-br ${b.tone} ${b.text} px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] shadow-sm`}
                  title={`${m}-day streak badge earned`}
                  data-testid={`streak-badge-${m}`}
                >
                  <Flame className="w-2.5 h-2.5" /> {b.label}
                </span>
              );
            })}
          </div>
        )}

        {/* 7-day rail */}
        <div className="flex items-center gap-1" data-testid="streak-week">
          {week.map((d, i) => {
            const isToday = i === week.length - 1;
            return (
              <span
                key={d.date}
                title={d.date}
                className={
                  "inline-block rounded-full transition " +
                  (d.active
                    ? "bg-pink-500 w-2.5 h-2.5"
                    : "bg-slate-200 w-2 h-2") +
                  (isToday && !d.active
                    ? " ring-2 ring-offset-1 ring-pink-300"
                    : "")
                }
                data-testid={`streak-day-${i}`}
                data-active={d.active ? "1" : "0"}
                data-today={isToday ? "1" : "0"}
              />
            );
          })}
        </div>

        {best > 0 && best !== current && (
          <span
            className="text-xs text-slate-500"
            data-testid="streak-best"
            title="Your best streak so far"
          >
            Best: {best} {best === 1 ? "day" : "days"}
          </span>
        )}
      </div>

      {reward && (
        <StreakRewardModal
          milestone={reward}
          onClose={() => setReward(null)}
        />
      )}
    </>
  );
}
